import { useMutation } from '@tanstack/react-query';
import { PricingCalculationResponse } from '../types';
import { ContractFormData, ContractGenerationResponse, ContractType, MANAGER_OPTIONS } from './types';

const CONTRACT_API_URL =
	'https://script.google.com/macros/s/AKfycbyLc46lCvK4B-LDkj8ZTOrIiyOHenNsQ2vg9zwvG3pwSHYMCZFt9Z8zBb028ZKwAOIR/exec';

/**
 * Success team email recipients
 * These emails will receive contract notifications for Success team contracts
 */
const SUCCESS_TEAM_RECIPIENTS = [
	'leia.stallings@levitateapp.com',
	'emily.coughlin@levitateapp.com',
	'courtney.manwarren@levitateapp.com',
	'jacob.wisniewski@levitateapp.com',
];

interface ContractPayload extends ContractFormData {
	contractType: ContractType;
	pricingData?: PricingCalculationResponse;
}

/**
 * API payload interface for contract generation
 * Matches the Google Apps Script expectations
 */
interface ContractApiPayload {
	// Shared fields (both new and current clients)
	clientType: 'new' | 'current';
	companyName: string;
	customerAddress: string;
	customerEmail: string;
	customerName: string;
	customerTitle: string;
	managerEmail: string;
	managerName: string;
	repEmail: string;
	repName: string;
	urgency: string;
	hipaaCompliance: boolean;
	isHippa: boolean; // AppScript legacy field
	additionalNotes: string;
	isSuccessTeam?: boolean; // Success team flag for template selection
	successTeamRecipients?: string[]; // Additional recipients for success team contracts

	// Add-ons (both new and current)
	AddOns: string;
	fbAdsChecked: boolean;
	fbPremiumChecked: boolean;
	gLSAChecked: boolean;
	customContentChecked: boolean;
	customContentNonprofit?: boolean;
	customContentNonprofitChecked?: boolean;
	handwritten: boolean;
	AddOnCost?: string;

	// New client specific fields
	planType?: string;
	numUsers?: string;
	billingCycle?: string;
	ncMrrPlanType?: string;
	NCbasePlanName?: string;
	NCwebsitePlan?: string;
	NCwebsitePrice?: string;
	NClevCorePrice?: string;
	NCimpFeePrice?: string;
	NCdiscountSubtotal?: string;
	NCdiscountPercentage?: string;
	NCpricingAnnual?: string;
	NCpricingQuarterly?: string;
	NCRenewalQrtPrice?: string;
	pricingMonthly?: string;
	totalCost?: string;
	renewalAnnualTotal?: string;

	// Legacy fields for backwards compatibility
	impFeePrice?: string;
	discountSubtotal?: string;
	discountPercentage?: string;
	pricingAnnual?: string;
	quarterlyCost?: string;

	// Fee fields
	facebookAdsFee?: string;
	googleLSAFee?: string;
	customContentFee?: string;

	// Current client specific fields
	adminLink?: string;
	currentLevitatePlan?: string;
	renewalDate?: string;
	monthsLeft?: string;
	contractTypeValue?: string;
	pendingCancel?: string;
	CCplanType?: string;
	CCrenewalDate?: string;
	CChowManyMonths?: string;
	CCpendingCancelSave?: string;
	CCtotalPriceDue?: string;
	CCtotalRenewalPrice?: string;
	CCDiscountBreakdown?: string;
	CCqrtPriceDueToday?: string;
	CCfullqrtRenewal?: string;
	CCfacebookAds?: string;
}

/**
 * Helper to format numbers as comma-separated strings for the API
 */
const formatForApi = (value: number | undefined): string => {
	const num = value ?? 0;
	return num.toLocaleString('en-US', {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	});
};

/**
 * Helper to build add-ons string
 */
const buildAddOnsString = (data: ContractFormData): string => {
	const addOns = [];
	if (data.fbAdsChecked) addOns.push('Meta Ads');
	if (data.fbPremiumChecked) addOns.push('Meta Premium');
	if (data.gLSAChecked) addOns.push('Google LSA');
	if (data.customContentChecked) addOns.push('Custom Content');
	if (data.customContentNonprofitChecked) addOns.push('Custom Content (Non-Profit)');
	if (data.handwritten) addOns.push('Handwritten Cards');
	return addOns.join(', ');
};

/**
 * Helper to calculate add-on cost for current clients
 */
const calculateAddOnCost = (data: ContractFormData): string => {
	let total = 0;
	if (data.fbAdsChecked) total += 100 * 12; // $100/month
	if (data.fbPremiumChecked) total += 249 * 12; // $249/month
	if (data.gLSAChecked) total += 100 * 12; // Approximate
	if (data.customContentChecked) total += 50 * 12; // Approximate
	if (data.customContentNonprofitChecked) total += 0; // No cost for non-profit
	return total > 0 ? formatForApi(total) : '';
};

/**
 * Helper: Extract website tier from plan name
 */
const extractWebsiteTier = (planType: string): string => {
	const plan = (planType || '').toLowerCase();
	if (plan.includes('essential')) return 'Essential Website';
	if (plan.includes('preferred')) return 'Preferred Website';
	if (plan.includes('prestige')) return 'Prestige Website';
	return '';
};

/**
 * Helper: Calculate website price (annual)
 */
const calculateWebsitePrice = (planType: string): number => {
	const plan = (planType || '').toLowerCase();
	const isStandalone = plan.includes('standalone');

	if (plan.includes('essential')) {
		return isStandalone ? 299 * 12 : 199 * 12; // $299 standalone, $199 bundled
	}
	if (plan.includes('preferred')) {
		return isStandalone ? 349 * 12 : 249 * 12; // $349 standalone, $249 bundled
	}
	if (plan.includes('prestige')) {
		return isStandalone ? 399 * 12 : 299 * 12; // $399 standalone, $299 bundled
	}
	return 0;
};

/**
 * Helper: Calculate Levitate core price (annual, NO onboarding, NO website addon)
 */
const calculateLevCorePrice = (planType: string, numUsers: number): number => {
	const defaultUsers = planType === 'Levitate Corporate' ? 10 : 5;
	const users = numUsers || defaultUsers;
	const plan = planType || '';

	// For standalone website plans, core Levitate price is 0
	if (plan.toLowerCase().includes('standalone')) {
		return 0;
	}

	// Determine the core Levitate monthly base cost (without website)
	let coreMonthlyBase = 0;

	if (plan.includes('$299 Client Happiness')) {
		coreMonthlyBase = 299;
	} else if (plan.includes('$349 Client Happiness')) {
		coreMonthlyBase = 349;
	} else if (plan.includes('$369 Client Happiness')) {
		coreMonthlyBase = 369;
	} else if (plan.includes('$399 Client Happiness Advanced')) {
		coreMonthlyBase = 399;
	} else if (plan.includes('Client Happiness Advanced + ') && plan.includes('Website')) {
		// All bundled website plans have $399 core (Advanced base)
		coreMonthlyBase = 399;
	} else if (plan.includes('(LGL HS FIN)')) {
		coreMonthlyBase = 399; // LGL HS FIN is also $399 core
	}

	const baseCost = coreMonthlyBase * 12;

	// Facebook Ads fee (if in plan name) - annual
	const facebookAdsFee = plan.toLowerCase().includes('facebook ads') ? 1200 : 0;

	// Extra user cost per month - varies by plan
	let extraUserCostPerMonth = 50; // default for $299 Client Happiness
	if (
		plan.includes('Platform w/ AI') ||
		plan.includes('Advanced') ||
		plan.includes('Website') ||
		plan.includes('$369')
	) {
		extraUserCostPerMonth = 55;
	}

	const additionalUsers = Math.max(users - 5, 0);
	const additionalCost = additionalUsers * extraUserCostPerMonth * 12;

	// Core price = base + extra users + FB ads (NO onboarding, NO website)
	const corePrice = baseCost + additionalCost + facebookAdsFee;

	return corePrice;
};

/**
 * Helper: Extract subscription name (remove price prefix)
 */
const extractSubscriptionName = (planTypeString: string): string => {
	if (!planTypeString) return '';
	// Remove "$299 - " or "$299 " prefix, keep only the plan name
	const match = planTypeString.match(/^\$\d+\s*-?\s*(.+)$/);
	return match ? match[1] : planTypeString;
};

/**
 * Helper: Extract core plan name (remove price AND website portion)
 */
const extractCorePlanName = (planTypeString: string): string => {
	if (!planTypeString) return '';
	// First remove price prefix
	let name = extractSubscriptionName(planTypeString);
	// Then remove website portion: " + Essential Website", " + Preferred Website", " + Prestige Website"
	name = name.replace(/\s*\+\s*(Essential|Preferred|Prestige)\s+Website/gi, '');
	return name;
};

/**
 * Helper: Build CC Facebook Ads string
 */
const buildCCFacebookAdsString = (data: ContractFormData): string => {
	const ads = [];
	if (data.fbAdsChecked) ads.push('Meta Ads ($100/month)');
	if (data.fbPremiumChecked) ads.push('Meta Premium ($249/month)');
	return ads.join(', ');
};

/**
 * Helper: Format CCplanType by removing " Upgrade" suffix
 * Example: "$299 Website Preferred Upgrade" -> "$299 Website Preferred"
 */
const formatCCPlanType = (contractTypeValue: string): string => {
	if (!contractTypeValue) return '';
	return contractTypeValue.replace(/\s+Upgrade\s*$/i, '');
};

/**
 * Hook to generate contracts using Google Apps Script
 */
export function useContractGeneration() {
	return useMutation<ContractGenerationResponse, Error, ContractPayload>({
		mutationFn: async (payload: ContractPayload) => {
			const { contractType, pricingData, ...formData } = payload;

			// Build rep email from first and last name
			const repEmail = `${formData.repFirstName.toLowerCase()}.${formData.repLastName.toLowerCase()}@levitateapp.com`;
			const repName = `${formData.repFirstName} ${formData.repLastName}`;

			// Get manager name from email
			const managerData = MANAGER_OPTIONS.find(m => m.email === formData.managerEmail);
			const managerName = managerData?.name || formData.managerEmail;

			// Determine client type for AppScript
			const clientType = contractType === 'new' ? 'new' : 'current';

			// Base payload with shared fields
			const contractPayload: ContractApiPayload = {
				clientType,
				companyName: formData.companyName,
				customerAddress: formData.customerAddress,
				customerEmail: formData.customerEmail,
				customerName: formData.customerName,
				customerTitle: formData.customerTitle,
				managerEmail: formData.managerEmail,
				managerName,
				repEmail,
				repName,
				urgency: formData.urgency,
				hipaaCompliance: formData.hipaaCompliance || false,
				isHippa: formData.hipaaCompliance || false, // AppScript uses 'isHippa'
				additionalNotes: formData.additionalNotes || '',
				isSuccessTeam: formData.isSuccessTeam || false, // Success team flag for template selection
				// Add-ons initialized (will be set properly below based on client type)
				AddOns: '',
				fbAdsChecked: false,
				fbPremiumChecked: false,
				gLSAChecked: false,
				customContentChecked: false,
				handwritten: false,
			};

			// Add Success team recipients if this is a Success team contract
			if (formData.isSuccessTeam) {
				contractPayload.successTeamRecipients = SUCCESS_TEAM_RECIPIENTS;
			}

			if (contractType === 'new' && pricingData) {
				// NEW CLIENT CONTRACT
				// Strip industry vertical suffix (e.g. "for Insurance", "for Non-Profit") from the plan name —
				// the vertical is irrelevant to the contract and shouldn't appear on the slide.
				// Also prepend the monthly price so the backend's NCMRR calculation uses the correct value
				// from the pricebook instead of recalculating from hardcoded plan rates.
				const basePlanType = (pricingData.planType || '').replace(/\s+for\s+.+$/i, '').trim();
				const monthlyMRR = Math.round((pricingData.renewalAnnualTotal ?? 0) / 12);
				// Only prepend price if plan name doesn't already have one (legacy plans like "$399 Client Happiness..." already carry their own price)
				const alreadyHasPrice = /^\$\d+/.test(basePlanType);
				contractPayload.planType = !alreadyHasPrice && monthlyMRR > 0 ? `$${monthlyMRR} ${basePlanType}` : basePlanType;
				contractPayload.numUsers = String(pricingData.numUsers ?? (basePlanType === 'Levitate Corporate' ? 10 : 5));
				contractPayload.billingCycle = pricingData.billingCycle || 'Annual';

				// NC specific merge fields
				const isStandalone = (pricingData.planType || '').toLowerCase().includes('standalone');
				contractPayload.ncMrrPlanType = pricingData.planType;
				contractPayload.NCbasePlanName = isStandalone ? '' : extractCorePlanName(pricingData.planType || '');
				contractPayload.NCwebsitePlan = extractWebsiteTier(pricingData.planType || '');
				contractPayload.NCwebsitePrice = formatForApi(calculateWebsitePrice(pricingData.planType || ''));

				// Calculate NClevCorePrice using actual MRR values from pricebook instead of hardcoded
				// Core price = base product annual + extra user annual (using actual MRR, not hardcoded $50-55/user)
				const extraUserAnnual = (pricingData.extraUserCostMRR ?? 0) * 12;
				// Use hardcoded base for now (TODO: get from pricebook), but use actual extra user cost
				const baseCoreAnnual = isStandalone ? 0 : calculateLevCorePrice(pricingData.planType || '', 5); // Base only (5 users = 0 extra)
				const levCorePrice = baseCoreAnnual + extraUserAnnual;
				contractPayload.NClevCorePrice = isStandalone ? '' : formatForApi(levCorePrice);
				contractPayload.NCimpFeePrice = formatForApi(pricingData.onboardingFee);
				contractPayload.NCdiscountSubtotal = formatForApi(pricingData.discountAmount);
				contractPayload.NCdiscountPercentage = pricingData.discountPercentage
					? `${pricingData.discountPercentage}%`
					: '';
				contractPayload.NCpricingAnnual = formatForApi(pricingData.totalBeforeTaxes);
				contractPayload.NCpricingQuarterly = formatForApi(pricingData.quarterlyTotal);
				contractPayload.NCRenewalQrtPrice = formatForApi((pricingData.renewalAnnualTotal ?? 0) / 4);
				contractPayload.pricingMonthly = formatForApi((pricingData.renewalAnnualTotal ?? 0) / 12);
				contractPayload.totalCost = formatForApi(pricingData.annualTotal);
				contractPayload.renewalAnnualTotal = formatForApi(pricingData.renewalAnnualTotal);

				// Legacy field names (for backwards compatibility with AppScript)
				contractPayload.impFeePrice = formatForApi(pricingData.onboardingFee);
				contractPayload.discountSubtotal = formatForApi(pricingData.discountAmount);
				contractPayload.discountPercentage = pricingData.discountPercentage ? `${pricingData.discountPercentage}%` : '';
				contractPayload.pricingAnnual = formatForApi(pricingData.totalBeforeTaxes);
				contractPayload.quarterlyCost = formatForApi(pricingData.quarterlyTotal);

				// Add-ons from pricing data
				contractPayload.fbAdsChecked = pricingData.fbAdsChecked || false;
				contractPayload.fbPremiumChecked = pricingData.fbAdsPremiumChecked || false;
				contractPayload.gLSAChecked = pricingData.gLSAChecked || false;
				contractPayload.customContentChecked = pricingData.customContentChecked || false;
				contractPayload.customContentNonprofit = pricingData.customContentNonprofit || false;
				contractPayload.handwritten = pricingData.handwritten || false;

				// Fees
				contractPayload.facebookAdsFee = formatForApi(pricingData.facebookAdsFee);
				contractPayload.googleLSAFee = formatForApi(pricingData.googleLSAFee);
				contractPayload.customContentFee = formatForApi(pricingData.customContentFee);

				// Calculate add-on cost for new clients
				let addOnTotal = 0;
				if (pricingData.facebookAdsFee) addOnTotal += pricingData.facebookAdsFee;
				if (pricingData.googleLSAFee) addOnTotal += pricingData.googleLSAFee;
				if (pricingData.customContentFee) addOnTotal += pricingData.customContentFee;
				contractPayload.AddOnCost = addOnTotal > 0 ? formatForApi(addOnTotal) : '';
			} else {
				// CURRENT CLIENT CONTRACT
				contractPayload.adminLink = formData.adminLink || '';
				contractPayload.currentLevitatePlan = formData.currentLevitatePlan || '';
				contractPayload.renewalDate = formData.renewalDate || '';
				contractPayload.monthsLeft = String(formData.monthsLeft ?? '');
				contractPayload.billingCycle = formData.billingCycle || 'Annual'; // REQUIRED for template selection
				contractPayload.contractTypeValue = formData.contractTypeValue || '';
				contractPayload.pendingCancel = formData.pendingCancel || 'no';

				// CC specific merge fields
				contractPayload.CCplanType = formatCCPlanType(formData.contractTypeValue || '');
				contractPayload.CCrenewalDate = formData.renewalDate;
				contractPayload.CChowManyMonths = String(formData.monthsLeft ?? '');
				contractPayload.CCpendingCancelSave = formData.pendingCancel || 'no';

				// New required fields
				contractPayload.CCtotalPriceDue = formatForApi(formData.totalPriceDueToday || 0);
				contractPayload.CCtotalRenewalPrice = formatForApi(formData.totalPriceUponRenewal || 0);
				contractPayload.CCDiscountBreakdown = formatForApi(formData.discountBreakdown || 0);

				// Calculate quarterly prices
				const totalDueToday = formData.totalPriceDueToday || 0;
				const totalRenewal = formData.totalPriceUponRenewal || 0;
				contractPayload.CCqrtPriceDueToday = formatForApi(totalDueToday / 4);
				contractPayload.CCfullqrtRenewal = formatForApi(totalRenewal / 4);

				// Add-ons from form
				contractPayload.fbAdsChecked = formData.fbAdsChecked || false;
				contractPayload.fbPremiumChecked = formData.fbPremiumChecked || false;
				contractPayload.gLSAChecked = formData.gLSAChecked || false;
				contractPayload.customContentChecked = formData.customContentChecked || false;
				contractPayload.handwritten = formData.handwritten || false;

				// CC Facebook Ads string
				contractPayload.CCfacebookAds = buildCCFacebookAdsString(formData);

				// Calculate add-on costs
				const addOnCost = calculateAddOnCost(formData);
				contractPayload.AddOnCost = addOnCost;
				contractPayload.customContentNonprofitChecked = formData.customContentNonprofitChecked || false;
				if (addOnCost) {
					contractPayload.facebookAdsFee = formData.fbAdsChecked ? formatForApi(100 * 12) : '';
					contractPayload.googleLSAFee = formData.gLSAChecked ? formatForApi(100 * 12) : '';
					contractPayload.customContentFee = formData.customContentChecked ? formatForApi(50 * 12) : '';
				}
			}

			// Add add-ons string (both new and current)
			contractPayload.AddOns = buildAddOnsString(formData);

			const response = await fetch(CONTRACT_API_URL, {
				body: JSON.stringify(contractPayload),
				headers: {
					'Content-Type': 'text/plain',
				},
				method: 'POST',
			});

			if (!response.ok) {
				throw new Error(`Failed to generate contract: ${response.statusText}`);
			}

			const data = await response.json();
			return data;
		},
	});
}
